import '../utils/index.less';
import Robot from '../utils/robot'
export interface GridOptions {
    url: string
    col: number
    row: number
    el: string | HTMLElement
    transitionTime?: number
    loadComplete?: () => void
    onBlockMove?: (step: number, moveDom: HTMLElement) => void
    success?: (score: number) => void
    beforeDestroy?: () => void
}

type gridPostioinOptions = Array<[number, number]>

export default class GridPicture {
    protected options: GridOptions = {
        url: '', //图片地址
        col: 3, //列数
        row: 3, //行数
        el: '', //拼图父容器
        transitionTime: 0.3, //移动动画时间
    }
    //拼图方块对应的坐标信息
    private gridPostioin: gridPostioinOptions = []
    //图片信息
    protected imageInfo = { width: 0, height: 0 }
    //拼图父容器
    public gridContainer: null | HTMLElement = null
    //拼图容器
    public gridDom: null | HTMLElement = null
    //拼图块的宽度
    protected widthUnit = 0
    //拼图块的高度
    protected heightUnit = 0
    //拼图块Dom集合
    protected blockDoms: Array<HTMLElement> = []
    //隐藏的拼图块dom
    protected hideBlock: null | HTMLElement = null
    //所用步数
    protected steps = 0

    constructor(options: GridOptions) {
        //合并配置项
        this.options = { ...this.options, ...options }
        this.initialize()
    }
    /**
     * 初始化
     */
    private async initialize() {
        const { url, col, row, el, loadComplete } = this.options
        if (!el) {
            throw new Error('el must be a string or HTMLElement')
        }
        this.imageInfo = await this.loadImage(url)
        //获取网格坐标系的二维数组
        this.gridPostioin = this.getGridPosition(col, row)
        //获取拼图父容器
        this.gridContainer = typeof el === 'string' ? document.querySelector(el) : el
        //创建拼图容器
        this.gridDom = this.createGridConainer()
        //添加拼图子元素
        this.gridDom?.appendChild(this.createGridChildren())
        //将拼图容器添加到父容器中
        this.gridContainer?.appendChild(this.gridDom)
        //设置拼图容器事件
        this.setBlockEvent()
        //执行加载完毕回调事件
        loadComplete?.()
    }
    /**
     * 创建拼图容器
     * @returns
     */
    private createGridConainer() {
        const { url, col, row, transitionTime } = this.options
        //获取目标坐标信息
        const container = document.createElement('div')
        const { width, height } = this.imageInfo
        this.widthUnit = width / col
        this.heightUnit = height / row
        //设置cssvar 变量
        container?.style.setProperty('--gridWidth', `${width}px`)
        container?.style.setProperty('--gridHeight', `${height}px`)
        container?.style.setProperty('--gridItemWidth', `${this.widthUnit}px`)
        container?.style.setProperty('--gridItemHeight', `${this.heightUnit}px`)
        container?.style.setProperty('--backgroundImage', `url('${url}')`)
        container?.style.setProperty('--transitionTime', `${transitionTime}s`)
        container?.classList.add('puzzle-grid')
        return container
    }
    /**
     * 创建拼图子元素
     * @returns
     */
    private createGridChildren(): DocumentFragment {
        const fragment = document.createDocumentFragment()
        this.gridPostioin.forEach((point, index) => {
            const [x, y] = point
            const div = document.createElement('div')
            div.style.backgroundPosition = `-${x * (this.widthUnit-1)}px -${y * (this.heightUnit-1)}px`
            div.setAttribute('data-index', String(index))
            div.classList.add('puzzle-grid-item')
            this.blockDoms.push(div)
            //设置拼图块的位置
            this.setBlockItemPosition(div, index)
            fragment.appendChild(div)
        })
        return fragment
    }
    /**
     * 更新一维数组对应索引DOM的位置
     * @param ele
     * @param index
     */
    private async setBlockItemPosition(ele: HTMLElement, index: number) {
        const [x, y] = this.getCoordinateByIndex(index)
        ele.style.left = `${x * this.widthUnit}px`
        ele.style.top = `${y * this.heightUnit}px`
    }
    /**
     * 设置隐藏块
     */
    private async setHideBlock() {
        this.hideBlock = this.blockDoms[this.blockDoms.length - 1]
        this.hideBlock.style.setProperty('display', 'none')
    }
    /**
     * 添加点击事件
     * @param e
     */
    private blockEvent = (e: Event) => {
        //如果拼图成功，则不再执行
        if (this.isSuccess()) {
            return
        }
        const target = e.target as HTMLElement
        const { onBlockMove } = this.options
        if (target.classList.contains('puzzle-grid-item')) {
            const curIndex = this.blockDoms.findIndex((item) => item === target)
            //检测当前块是否可以移动
            if (this.canMove(curIndex)) {
                this.steps++
                this.moveBlock(curIndex)
                onBlockMove?.(this.steps, target)
            }
        }
    }
    /**
     * 添加点击事件
     */
    private setBlockEvent() {
        this.gridDom?.removeEventListener('click', this.blockEvent)
        this.gridDom?.addEventListener('click', this.blockEvent, false)
    }
    /**
     * 移动方块
     * @param index 当前点击块的索引
     * @param trigger 是否触发完成回调
     */
    public moveBlock(index: number, trigger = true): void {
        const targetDom = this.blockDoms[index]
        const hideIndex = this.getHideIndex()
        //交换一维DOM数组位置
        this.blockDoms[hideIndex] = targetDom
        this.blockDoms[index] = this.hideBlock!
        //交换style位置信息
        this.setBlockItemPosition(targetDom, hideIndex)
        this.setBlockItemPosition(this.hideBlock!, index)
        if (this.isSuccess() && trigger) {
            //执行成功回调
            this.handleSuccess()
        }
    }
    /**
     * 拼图成功
     */
    private async handleSuccess() {
        const { success, transitionTime = 0.3 } = this.options
        //等待动画执行完毕
        await this.sleep(transitionTime * 1000)
        this.hideBlock?.style.setProperty('display', 'block')
        this.hideBlock = null
        success?.(this.steps)
    }

    /**
     * 当前块是否可以移动
     * @param index
     * @returns
     */
    private canMove(index: number): boolean {
        const distance = this.getCoordDistance(
            this.getCoordinateByIndex(this.getHideIndex()),
            this.getCoordinateByIndex(index)
        )
        return distance === 1
    }
    /**
     * 根据索引获取坐标信息
     * @param index
     * @returns
     */
    private getCoordinateByIndex(index: number): [number, number] {
        return this.gridPostioin[index]
    }
    /**
     * 获取两个点之间的距离
     * @param a
     * @param b
     * @returns
     */
    private getCoordDistance(a: [number, number], b: [number, number]): number {
        const [x1, y1] = a
        const [x2, y2] = b
        return Math.abs(x1 - x2) + Math.abs(y1 - y2)
    }
    /**
     * 获取目标坐标信息
     * @param col
     * @param row
     * @returns
     */
    private getGridPosition(col: number, row: number): gridPostioinOptions {
        const result: gridPostioinOptions = []
        for (let i = 0; i < col; i++) {
            for (let j = 0; j < row; j++) {
                result.push([i, j])
            }
        }
        //排序 以[[0,0],[1,0],[2,0]]的坐标系形式进行排列
        return result.sort((a, b) => a[1] - b[1])
    }
    /**
     * sleep函数
     * @param time
     * @returns
     */
    private sleep(time = 200): Promise<void> {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                resolve()
            }, time)
        })
    }
    /**
     * 随机设置位置
     */
    private async randomSetPosition() {
        const { col, row } = this.options
        //复原步数与方阵的阶数有关，无论打乱多少次，复原步数都是趋于一个稳定的范围。
        const shuffleCount = col * row * 10
        let moveBlock: null | HTMLElement = null
        for (let i = 0; i < shuffleCount; i++) {
            //获取当前可移动的拼图块 并排除上次已经移动的块
            const canMoveList = this.blockDoms.filter(
                (block, index) => this.canMove(index) && block !== moveBlock
            )
            moveBlock = canMoveList[Math.floor(Math.random() * canMoveList.length)]
            const index = this.blockDoms.findIndex((item) => item === moveBlock)
            this.moveBlock(index, false)
        }
        //如果打乱后为成功状态 则继续打乱
        if (this.isSuccess()) {
            this.randomSetPosition()
        }
    }
    /**
     * 获取隐藏DOM的索引
     */
    private getHideIndex() {
        return this.blockDoms.findIndex((item) => item === this.hideBlock)
    }
    /**
     * 当前拼图是否完成
     */
    private isSuccess() {
        return this.blockDoms.every(
            (dom, index) =>
                this.getCoordinateByIndex(Number(dom.getAttribute('data-index'))).join(',') ===
                this.getCoordinateByIndex(index).join(',')
        )
    }
    /**
     * 获取图片信息
     * @param url
     * @returns
     */
    private loadImage(url: string): Promise<{ width: number; height: number }> {
        return new Promise((resolve, reject) => {
            if (!url) {
                return reject('image url must be a string')
            }
            const image = new Image()
            image.src = url
            image.onload = () => {
                const { width, height } = image
                resolve({
                    width,
                    height,
                })
            }
            image.onerror = () => {
                reject('load image error')
            }
        })
    }
    /**
     * 开始游玩
     */
    public play() {
        this.reset()
    }

    /**
     * 重置拼图位置
     */
    public reset() {
        this.steps = 0
        if (!this.hideBlock) {
            this.setHideBlock()
        }
        this.randomSetPosition()
    }
    /**
     * 销毁当前实例
     */
    public destroy() {
        const { beforeDestroy } = this.options
        beforeDestroy && beforeDestroy()
        //移除事件
        this.gridDom?.removeEventListener('click', this.blockEvent, false)
        //移除DOM
        this.gridContainer?.removeChild(this.gridDom as HTMLElement)
        //重置变量
        this.gridContainer = null
        this.gridDom = null
        this.blockDoms = []
        this.steps = 0
        this.hideBlock = null
        this.imageInfo = { width: 0, height: 0 }
        this.gridPostioin = []
    }
    /**
    * 自动寻路算法
    */
    public async autoMove() {
        const { onBlockMove, col, row, } = this.options
        //暂停一下，显示正在计算中标题
        await new Promise((resolve) => setTimeout(resolve, 1500))
        const autoRobot = new Robot(
            this.blockDoms.map((item) => Number(item.dataset.index)),
            row,
            col
        )
        const moveRes = autoRobot.crackRun()
        this.steps = 0
        await Promise.all(
            moveRes.map((e: any, index: number) => {
                return new Promise((resolve, reject) => {
                    setTimeout(() => {
                        this.moveBlock(e.from - 1)
                        this.steps++
                        onBlockMove?.(this.steps, e)
                        resolve(1)
                    }, 600 * index)

                })
            })
        )
    }
}
